import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";
import { Button, SearchField } from "@heroui/react";
import { contractorsStore } from "@/stores/ContractorsStore";
import { serviceCenterFormStore } from "@/stores/ServiceCenterFormStore";
import MetricsPanel from "./components/MetricsPanel";
import PayoutBlock from "./components/PayoutBlock";
import FilterPanel from "./components/FilterPanel";
import SortPanel from "./components/SortPanel";
import MonthNavigator from "./components/MonthNavigator";
import ContractorsTable from "./components/ContractorsTable";
import PayoutModal from "./components/PayoutModal";
import { ServiceCenterDrawer } from "@/features/constractors";
import "./ContractorsPage.css";

const ContractorsPage: React.FC = observer(() => {
  const [isPayoutOpen, setIsPayoutOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(() => contractorsStore.search);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isInitialMount = useRef(true);

  useEffect(() => {
    contractorsStore.loadAll();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Помечаем, что пользователь активно печатает — UI не должен
    // блокироваться по isFetching, пока ввод не «осел».
    contractorsStore.setIsUserTypingSearch(true);
    debounceRef.current = setTimeout(() => {
      contractorsStore.setIsUserTypingSearch(false);
      contractorsStore.setSearch(searchInput);
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const hasSelection = contractorsStore.selectedRows.size > 0;

  const payoutRecipients = contractorsStore.selectedItems
    .filter((c) => c.pendingPayout > 0)
    .map((c) => ({
      name: c.name,
      address: c.address,
      amount: c.pendingPayout,
    }));

  return (
    <div className="sc-page" data-testid="contractors-page">
      {/* Toolbar */}
      <div className="sc-page__toolbar">
        <div className="sc-page__toolbar-left">
          <div className="sc-page__search" data-testid="contractors-search">
            <SearchField
              value={searchInput}
              onChange={(v) => setSearchInput(v)}
              aria-label="Поиск сервисных центров"
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input placeholder="Поиск..." />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          </div>

          <FilterPanel />
          <SortPanel />
          <MonthNavigator />
        </div>
        <Button
          variant="primary"
          aria-label="Добавить партнёра"
          data-testid="contractors-add-button"
          onPress={() => serviceCenterFormStore.openCreate()}
        >
          <Icon path={mdiPlus} size={0.85} />
          Добавить партнёра
        </Button>
      </div>

      {/* Metrics / Payout block */}
      <div className="sc-metrics-area">
        {hasSelection ? (
          <PayoutBlock onPayout={() => setIsPayoutOpen(true)} />
        ) : (
          <MetricsPanel />
        )}
      </div>

      {/* Table */}
      <div className="sc-page__table-wrap" data-testid="contractors-table-wrap">
        <ContractorsTable />
      </div>

      {/* Payout modal */}
      <PayoutModal
        isOpen={isPayoutOpen}
        onOpenChange={setIsPayoutOpen}
        recipients={payoutRecipients}
        totalAmount={contractorsStore.totalDebt}
        onSuccess={() => contractorsStore.clearSelection()}
      />

      {/* Service center create/edit drawer */}
      <ServiceCenterDrawer onSuccess={() => contractorsStore.loadAll()} />
    </div>
  );
});

ContractorsPage.displayName = "ContractorsPage";

export default ContractorsPage;
